import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useData } from '../lib/data';
import type { Task, TaskPriority } from '../lib/database.types';
import { ProjectPicker } from './ProjectPicker';
import { formatDueInputValue, parseDueInputValue } from './DueChip';

// Inline add-task form. Used from ClientDashboard and ProjectView.
// Starts collapsed as a big "+ Add task" button, expands on click.
// The `defaultProjectSlug` pre-fills the project when launched from a
// ProjectView so you don't have to pick it. When launched from a client
// dashboard, the picker is scoped to that client's projects.
export function AddTaskForm({
  defaultProjectSlug,
  clientSlug,
  parentTaskId,
}: {
  defaultProjectSlug?: string;
  clientSlug?: string;
  parentTaskId?: string;
}) {
  const { projects, upsertTaskLocal } = useData();
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [projectSlug, setProjectSlug] = useState(defaultProjectSlug ?? '');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [dueAt, setDueAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expanded) setTimeout(() => inputRef.current?.focus(), 10);
  }, [expanded]);

  useEffect(() => {
    if (defaultProjectSlug) setProjectSlug(defaultProjectSlug);
  }, [defaultProjectSlug]);

  const reset = () => {
    setTitle('');
    setPriority('medium');
    setDueAt(null);
    setError(null);
    if (!defaultProjectSlug) setProjectSlug('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !projectSlug) return;
    if (READONLY) {
      reset();
      setExpanded(false);
      return;
    }
    setSaving(true);
    setError(null);
    const id = `${projectSlug.slice(0, 3)}-${Math.floor(Date.now() / 1000)}`;
    // Request the inserted row back so we can optimistically add it to local
    // state. Supabase realtime doesn't reliably broadcast INSERTs on tb_tasks
    // so we can't wait for the channel event.
    const { data, error: err } = await supabase
      .from('tb_tasks')
      .insert({
        id,
        project_slug: projectSlug,
        parent_task_id: parentTaskId ?? null,
        title: title.trim(),
        status: 'pending',
        priority,
        due_at: dueAt,
        autonomous: false,
        tags: [],
        blocked_by: [],
        execution_target: 'me',
        description: '',
        done_check: '',
        notes: '',
      })
      .select()
      .single();
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    if (data) upsertTaskLocal(data as Task);
    reset();
    setExpanded(false);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full flex items-center gap-3 px-4 py-4 panel text-left
                   hover:border-amber/60 hover:bg-panelHi/60 transition-colors group"
      >
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-md
                     bg-amber border border-amber text-bg font-mono text-xl font-bold
                     group-hover:brightness-110 shrink-0"
        >
          +
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {parentTaskId ? 'Add subtask' : 'Add task'}
          </div>
          <div className="text-xs text-muted font-mono mt-0.5">
            click to expand · or press <kbd className="px-1 border border-border rounded text-[10px]">N</kbd>
          </div>
        </div>
      </button>
    );
  }

  const scopedProjects = clientSlug
    ? projects.filter((p) => p.client_slug === clientSlug)
    : projects;

  return (
    <form onSubmit={submit} className="panel p-4 space-y-3">
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="task title…"
        className="w-full px-4 py-3 bg-panelHi border border-border rounded-md
                   text-ink placeholder-dim text-base
                   focus:outline-none focus:border-amber/60 transition-colors"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {!defaultProjectSlug && (
          <ProjectPicker
            value={projectSlug}
            onChange={setProjectSlug}
            projects={scopedProjects}
            defaultClientSlug={clientSlug ?? null}
          />
        )}
        <PriorityButtons value={priority} onChange={setPriority} />
      </div>

      <DueDateQuickPicker value={dueAt} onChange={setDueAt} />

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={() => {
            reset();
            setExpanded(false);
          }}
          className="text-xs text-dim hover:text-ink font-mono px-3 py-2"
        >
          cancel · esc
        </button>
        <button
          type="submit"
          disabled={saving || !title.trim() || !projectSlug}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold
                     bg-amber border border-amber text-bg
                     hover:brightness-110 transition
                     disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
        >
          {saving ? 'adding…' : parentTaskId ? 'add subtask' : 'add task'}
        </button>
      </div>

      {error && <div className="text-xs text-red font-mono">{error}</div>}
    </form>
  );
}

function PriorityButtons({
  value,
  onChange,
}: {
  value: TaskPriority;
  onChange: (v: TaskPriority) => void;
}) {
  const priorities: TaskPriority[] = ['critical', 'high', 'medium', 'low'];
  return (
    <div className="flex gap-1">
      {priorities.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={
            'flex-1 px-3 py-3 rounded-md text-xs font-mono uppercase tracking-wider min-h-[44px] transition-colors ' +
            (value === p
              ? `chip chip-${p} opacity-100 !font-bold`
              : 'bg-panelHi border border-border text-dim hover:border-borderHi')
          }
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function DueDateQuickPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  // Quick presets + native date picker. The presets are touch-friendly chips
  // for the common cases; the native input handles anything else.
  const today = () => {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    return d.toISOString();
  };
  const tomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(23, 59, 0, 0);
    return d.toISOString();
  };
  const endOfWeek = () => {
    const d = new Date();
    const daysUntilFriday = (5 - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + daysUntilFriday);
    d.setHours(23, 59, 0, 0);
    return d.toISOString();
  };

  const presets = [
    { label: 'today', iso: today() },
    { label: 'tomorrow', iso: tomorrow() },
    { label: 'end of week', iso: endOfWeek() },
  ];

  const isSelected = (iso: string): boolean => {
    if (!value) return false;
    return value.slice(0, 10) === iso.slice(0, 10);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] font-mono text-dim uppercase tracking-widest">due</span>
      {presets.map((p) => (
        <button
          key={p.label}
          type="button"
          onClick={() => onChange(isSelected(p.iso) ? null : p.iso)}
          className={
            'px-3 py-2 rounded-md text-xs font-mono min-h-[36px] transition-colors ' +
            (isSelected(p.iso)
              ? 'bg-amber border border-amber text-bg font-semibold'
              : 'bg-panelHi border border-border text-muted hover:border-borderHi')
          }
        >
          {p.label}
        </button>
      ))}
      <input
        type="date"
        value={formatDueInputValue(value)}
        onChange={(e) => onChange(parseDueInputValue(e.target.value))}
        className="px-3 py-2 bg-panelHi border border-border rounded-md text-xs font-mono text-ink
                   focus:outline-none focus:border-amber/60"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-dim hover:text-red px-2"
          aria-label="clear due date"
        >
          ×
        </button>
      )}
    </div>
  );
}
